import { useState, useRef, useEffect, useCallback } from 'react'
import { post } from '../lib/api'
import Icon from './Icon'
import { t } from '../lib/i18n'
import CommandBuilder from './CommandBuilder'
import type { Proposal } from '../lib/agent'

/**
 * Gasp of God — the admin command line, summoned.
 *
 * One input, plain instructions, and a junior developer on the other end who
 * does the safe obvious things and escalates the rest. It is always the last
 * thing on the screen and always focusable with `/`, because the whole point is
 * that acting on what you just read costs one keystroke rather than a trip to
 * a form.
 *
 * The reply is deliberately verbose about what it DID: "set to Complete, the
 * workbook still says Not Started" is the sentence that stops an override
 * quietly becoming a fact.
 */

/*
 * The Web Speech API, narrowly.
 *
 * It is still vendor-prefixed in Safari and absent from TypeScript's DOM lib,
 * so this declares only the four members used. Dictation happens IN THE BROWSER
 * — no audio is uploaded, and there is no transcription bill.
 */
interface SpeechLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  start(): void
  stop(): void
  onresult: ((e: SpeechResultLike) => void) | null
  onend: (() => void) | null
  onerror: ((e: { error?: string }) => void) | null
}
interface SpeechResultLike {
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>
}
type SpeechCtor = new () => SpeechLike

const speechCtor = (): SpeechCtor | null => {
  const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

type HelpDoc = { title: string; groups: Array<{ name: string; lines: string[][] }>; ids: string; truth: string }
type Reply = {
  ok: boolean; outcome: 'ok' | 'refused' | 'escalated' | 'error'; message: string
  ticketId?: string; lastRef?: string | null
  detail?: { help?: HelpDoc; entity?: string; ref?: string; row?: Record<string, unknown>
    overrides?: Array<{ field: string; value: string }>; notes?: Array<{ body: string; created_at: number }> }
  /** Set when Ollama turned prose into the command that actually ran. */
  translatedFrom?: string
}

const FIELD_LABEL: Record<string, string> = {
  task: 'Task', workstream: 'Workstream', owner: 'Owner', due_on: 'Due', status: 'Status',
  dependency: 'Depends on', evidence: 'Evidence', next_action: 'Next action', risk_if_late: 'Risk if late',
  name: 'Gate', pass_condition: 'Pass condition', if_not_passed: 'If not passed',
  risk: 'Risk', impact: 'Impact', likelihood: 'Likelihood', mitigation: 'Mitigation',
  where_to_fix: 'Where to fix', note: 'Note',
}

function RowAnswer({ detail }: { detail: NonNullable<Reply['detail']> }) {
  const row = detail.row ?? {}
  const overridden = new Set((detail.overrides ?? []).map((o) => o.field))
  const entries = Object.entries(row).filter(([k, v]) => k !== 'ref' && k !== 'sort' && v !== null && v !== '')
  return (
    <div className="vog-answer">
      <div className="vog-answer-head">{detail.ref}</div>
      <dl>
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt>{FIELD_LABEL[k] ?? k}</dt>
            <dd>
              {String(v)}
              {overridden.has(k) && <span className="cc-ovr">set here</span>}
            </dd>
          </div>
        ))}
      </dl>
      {(detail.notes ?? []).length > 0 && (
        <div className="vog-notes">
          <strong>Notes</strong>
          {(detail.notes ?? []).map((n, i) => (
            <p key={i}>{n.body} <span className="cc-sub">{new Date(n.created_at).toLocaleDateString()}</span></p>
          ))}
        </div>
      )}
    </div>
  )
}

function HelpPanel({ help }: { help: HelpDoc }) {
  return (
    <div className="vog-help">
      {help.groups.map((g) => (
        <div key={g.name} className="vog-help-group">
          <strong>{g.name}</strong>
          <dl>
            {g.lines.map(([cmd, what]) => (
              <div key={cmd}><dt><code>{cmd}</code></dt><dd>{what}</dd></div>
            ))}
          </dl>
        </div>
      ))}
      <p className="cc-sub">{help.ids}</p>
      <p className="cc-sub">{help.truth}</p>
    </div>
  )
}

export default function VoiceOfGod({ open, onOpenChange, onChanged }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onChanged: () => void
}) {
  const [command, setCommand] = useState('')
  const [busy, setBusy] = useState(false)
  const [replies, setReplies] = useState<Array<Reply & { raw: string }>>([])
  // The row the conversation is currently about, so "it done" works after
  // "tell me about C02". Held on the client: a server-side conversation would
  // be one more thing to expire and get out of step with what is on screen.
  const [lastRef, setLastRef] = useState<string | null>(null)
  // Whether the interpreter answered last time. Not probed on mount: it lives
  // on the server now, so the only honest test is asking it something.
  const [llm, setLlm] = useState<{ up: boolean; tried: boolean }>({ up: true, tried: false })
  const [thinking, setThinking] = useState(false)
  // What the agent proposes to do, waiting to be confirmed. Reads never land
  // here — only the things that write.
  const [pending, setPending] = useState<Proposal | null>(null)
  const input = useRef<HTMLInputElement>(null)

  /*
   * ── Summoned, not always there ───────────────────────────────────────────
   *
   * This was a panel pinned to the page, which meant it occupied the screen on
   * every visit whether or not anyone wanted it — and on a phone it sat between
   * the numbers and the task list. It is a modal now: absent until called,
   * closed with Escape, the backdrop, or the button.
   *
   * A native <dialog> rather than a div with a z-index: showModal() brings the
   * focus trap, the inert background, the backdrop and Escape-to-close with it,
   * and every one of those is a thing a hand-rolled modal gets wrong.
   */
  /*
   * Open state is the PARENT's, not this component's.
   *
   * The masthead button used to fire a `ridge:ask` window event that this
   * component listened for. That listener is attached in an effect, and effects
   * run after paint — so a click landing in the gap between the button being
   * painted and the listener existing was simply dropped, and nothing retried
   * it. Rare on a laptop, reliable on a slower machine, and invisible in both
   * because the button looks like it worked.
   *
   * A prop has no such window. The button sets state; React does the rest.
   */
  const setOpen = onOpenChange
  const dialog = useRef<HTMLDialogElement>(null)
  const [listening, setListening] = useState(false)
  const [building, setBuilding] = useState(false)
  const recognition = useRef<SpeechLike | null>(null)

  useEffect(() => {
    const el = dialog.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
    if (open) {
      window.setTimeout(() => input.current?.focus(), 30)
      return
    }
    /*
     * BLUR ON CLOSE.
     *
     * A closed <dialog> is display:none, but the input inside it can keep the
     * focus — so the page has a focused control nobody can see, and the next
     * "/" is swallowed by the "are you already typing?" guard and never
     * summons anything. Pressing Escape once made the shortcut dead until you
     * clicked somewhere. Found by the e2e test on its third close path.
     */
    input.current?.blur()
  }, [open])

  useEffect(() => {
    // "/" summons it from anywhere, unless you are already typing.
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      if (e.key === '/' && !typing) {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setOpen])

  const execute = useCallback(async (raw: string, translatedFrom?: string) => {
    const reply = await post<Reply>('/api/voice', { command: raw, lastRef })
    setReplies((r) => [{ ...reply, raw, translatedFrom }, ...r].slice(0, 6))
    if (reply.lastRef !== undefined) setLastRef(reply.lastRef)
    // Refresh whatever the command changed, so the screen never disagrees with
    // the thing it just told you it did.
    onChanged()
  }, [lastRef, onChanged])

  /*
   * Press to talk, release by stopping — and it SENDS ITSELF.
   *
   * The final transcript is submitted without another press, because the point
   * of dictating is not having to touch the thing. Interim results stream into
   * the input so you can see it hearing you; a wrong word is caught before it
   * is sent rather than after.
   */
  function toggleMic() {
    if (listening) {
      recognition.current?.stop()
      return
    }
    const Ctor = speechCtor()
    if (!Ctor) return
    const rec = new Ctor()
    rec.lang = document.documentElement.lang === 'es' ? 'es-PR' : 'en-US'
    rec.continuous = false
    rec.interimResults = true
    rec.onresult = (e) => {
      let text = ''
      let done = false
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i]![0]!.transcript
        if (e.results[i]!.isFinal) done = true
      }
      setCommand(text)
      if (done) {
        rec.stop()
        void submit(text)
      }
    }
    rec.onend = () => { setListening(false); recognition.current = null }
    rec.onerror = () => { setListening(false); recognition.current = null }
    recognition.current = rec
    setListening(true)
    rec.start()
  }

  function send(e: React.FormEvent) {
    e.preventDefault()
    void submit(command)
  }

  /*
   * Split from the form handler so DICTATION can submit without one.
   *
   * The mic finishes with a final transcript and nothing to click; routing that
   * through a synthetic submit event would be pretending a form was used.
   */
  async function submit(text: string) {
    const raw = text.trim()
    if (!raw || busy) return
    setBusy(true)
    try {
      /*
       * Ask the grammar first, without writing anything.
       *
       * A dry run is what makes the model optional: if the sentence already
       * parses, nothing is translated and nothing is slower. Only the input
       * that would have become a ticket goes to Ollama.
       */
      // Does the grammar already understand it? Nothing to interpret if so.
      const dry = await post<Reply>('/api/voice', { command: raw, lastRef, dryRun: true })
      if (dry.ok) {
        await execute(raw)
        setCommand('')
        return
      }

      /*
       * Prose. Ask the interpreter what it thinks this is.
       *
       * A read happens at once — showing a row changes nothing, and confirming
       * a question would be theatre. Anything that WRITES is proposed and
       * waits, so the operator sees the row it chose before it happens.
       */
      setThinking(true)
      let proposal: Proposal | null = null
      let available = false
      let answered: string | null = null
      try {
        const out = await post<{ proposal: Proposal | null; available: boolean; answer?: string | null }>(
          '/api/voice/interpret', { command: raw },
        )
        proposal = out.proposal
        available = out.available
        answered = out.answer ?? null
      } catch {
        available = false
      }
      setThinking(false)
      setLlm({ up: available, tried: true })

      /*
       * A question gets an answer, not a row.
       *
       * Every read used to end in show_row, so "where do I send these credit
       * reports?" printed a task and answered nothing. Routing is not replying.
       */
      if (answered) {
        const said: Reply & { raw: string } = { ok: true, outcome: 'ok', message: answered, raw }
        setReplies((r) => [said, ...r].slice(0, 6))
        setCommand('')
        return
      }

      if (!proposal) {
        /*
         * Say so, and do NOT quietly file a ticket.
         *
         * The old path ran the raw sentence when there was no interpreter,
         * which produced a grammar error AND a ticket for every question asked
         * — twice reported, both times looking like a broken parser rather than
         * an interpreter that had never been reachable.
         */
        const why = available
          ? 'I could not work out which row you meant.'
          : 'The interpreter did not answer, so only exact commands work right now.'
        const refusal: Reply & { raw: string } = {
          ok: false, outcome: 'refused', raw,
          message: `${why} Type help, or start with a row id like C01.`,
        }
        setReplies((r) => [refusal, ...r].slice(0, 6))
        return
      }

      // Checked against the grammar before it is offered, so an invented row id
      // never reaches a confirm card.
      const check = await post<Reply>('/api/voice', { command: proposal.command, lastRef, dryRun: true })
      if (!check.ok) {
        const bad: Reply & { raw: string } = {
          ok: false, outcome: 'refused', raw,
          message: `It suggested "${proposal.command}", which is not valid. Nothing was changed.`,
        }
        setReplies((r) => [bad, ...r].slice(0, 6))
        return
      }

      if (proposal.immediate) {
        await execute(proposal.command, raw)
        setCommand('')
      } else {
        setPending(proposal)
      }
    } catch (err) {
      const failure: Reply & { raw: string } = {
        ok: false, outcome: 'error', message: err instanceof Error ? err.message : 'Failed.', raw,
      }
      setReplies((r) => [failure, ...r].slice(0, 6))
    } finally {
      setBusy(false)
      setThinking(false)
    }
  }

  const micAvailable = typeof window !== 'undefined' && speechCtor() !== null

  return (
    <dialog
      className="gog"
      ref={dialog}
      aria-label={t('gog.title')}
      /*
       * Escape is INTERCEPTED, not merely observed.
       *
       * A <dialog> closes itself on Escape immediately, in the DOM, while
       * React's state catches up a tick later. Anything happening in that gap
       * sees open=true and a closed dialog — and "/" landing there set open to
       * true when it already was, so no state changed, no effect ran, and the
       * pending close then set it false. The shortcut was swallowed and the
       * modal stayed shut. Reliable on a slow machine, invisible on a fast one.
       *
       * Cancelling the native close and going through state instead keeps the
       * DOM and the state in lockstep, so there is no gap to land in.
       */
      onCancel={(e) => { e.preventDefault(); setOpen(false) }}
      onClose={() => setOpen(false)}
      /* The backdrop is the dialog's own box, so a click that lands on the
         element itself — never on a child — is a click outside the card. */
      onClick={(e) => { if (e.target === dialog.current) setOpen(false) }}
    >
      <div className="gog-card">
        <div className="gog-head">
          <div>
            <h2>{t('gog.title')}</h2>
            <span className="muted gog-hint">
              <kbd>/</kbd> summons · type <button type="button" className="link" onClick={() => { setCommand('help'); input.current?.focus() }}>help</button>
              {lastRef && <> · talking about <strong>{lastRef}</strong></>}
              {llm.tried && !llm.up && <> · <span className="vog-down">interpreter unavailable</span></>}
            </span>
          </div>
          <button type="button" className="gog-x" onClick={() => setOpen(false)} aria-label={t('gog.close')}>
            <Icon name="close" size={16} />
          </button>
        </div>

        <form onSubmit={send} className="vog-form">
          <input
            ref={input}
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder={listening ? t('gog.listening') : lastRef ? `ask about ${lastRef}, or "it done" · type help` : 'tell me about C01 · C01 done · help'}
            aria-label="Command"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
          {/* Absent rather than disabled where the browser has no speech API:
              a dead mic invites a press that can never work. */}
          {micAvailable && (
            <button
              type="button"
              className={`gog-mic ${listening ? 'on' : ''}`}
              onClick={toggleMic}
              aria-pressed={listening}
              aria-label={listening ? t('gog.stop') : t('gog.mic')}
              title={listening ? t('gog.stop') : t('gog.mic')}
            >
              <Icon name="mic" size={18} />
            </button>
          )}
          <button type="submit" disabled={busy || !command.trim()}>{thinking ? 'reading…' : busy ? '…' : 'Do it'}</button>
        </form>

        {/*
          * Dial it instead of remembering it.
          *
          * Collapsed by default: someone who knows the grammar should not have
          * to scroll past a keypad to reach the box. Someone who does not had
          * no way in at all except typing `help` and reading the syntax.
          */}
        <button
          type="button"
          className="gog-toggle"
          aria-expanded={building}
          onClick={() => setBuilding((v) => !v)}
        >
          <Icon name="chevron" size={14} /> {building ? t('gog.buildHide') : t('gog.build')}
        </button>
        {building && <CommandBuilder onBuild={(c) => { setCommand(c); input.current?.focus() }} />}

      {pending && (
        <div className="vog-confirm" role="alertdialog" aria-label="Confirm this change">
          <div className="vog-confirm-what">
            <strong>{pending.says}</strong>
            <code>{pending.command}</code>
          </div>
          <div className="vog-confirm-do">
            <button
              type="button"
              onClick={async () => {
                const p = pending
                setPending(null)
                setBusy(true)
                try { await execute(p.command, command.trim()); setCommand('') } finally { setBusy(false) }
              }}
            >
              Do it
            </button>
            <button type="button" className="link" onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}


      {replies.length > 0 && (
        <ul className="vog-log">
          {replies.map((r, i) => (
            <li key={i} className={`vog-reply vog-${r.outcome}`}>
              {r.translatedFrom && <span className="vog-from">you said: {r.translatedFrom}</span>}
              <code>{r.raw}</code>
              <span>{r.message}</span>
              {r.detail?.help && <HelpPanel help={r.detail.help} />}
              {r.detail?.row && <RowAnswer detail={r.detail} />}
            </li>
          ))}
        </ul>
      )}
      </div>
    </dialog>
  )
}
